/* eslint-disable @typescript-eslint/no-explicit-any */

const parseIdLike = (value: any): number | null => {
  if (value === null || value === undefined) return null;
  if (typeof value === "number" && !Number.isNaN(value)) return value;

  if (typeof value === "string") {
    const direct = Number(value);
    if (!Number.isNaN(direct) && direct > 0) return direct;

    const match = value.match(/\/(\d+)(?:\/)?$/);
    if (match?.[1]) return Number(match[1]);
  }

  if (typeof value === "object") {
    const fromId = parseIdLike((value as any).id);
    if (fromId) return fromId;

    const fromIri = parseIdLike((value as any)["@id"]);
    if (fromIri) return fromIri;
  }

  return null;
};

const asArray = (value: any): any[] => {
  return Array.isArray(value) ? value : [];
};

const normalizeFinancer = (value: any) => {
  if (!value || typeof value !== "object") return null;

  const fullname =
    value.fullname ??
    value.structureName ??
    value.name ??
    value.firstname ??
    value.lastname ??
    "";

  return {
    ...value,
    fullname,
    structureName: value.structureName ?? fullname,
  };
};

const readFinancerFromPaid = (value: any) => {
  if (!value || typeof value !== "object") return null;

  const invoiced =
    value?.invoiced ??
    value?.verdeoFundingInvoiced ??
    value?.verdeo_funding_invoiced ??
    null;
  const financer = invoiced?.financer ?? invoiced?.financier ?? null;
  return normalizeFinancer(financer);
};

const readInvoicedIdFromDesiredFunding = (desiredFunding: any): number | null => {
  return parseIdLike(
    desiredFunding?.indicatorValue?.verdeoFundingPaid?.invoiced ??
      desiredFunding?.indicatorValue?.verdeo_funding_paid?.invoiced ??
      desiredFunding?.verdeoFundingPaid?.invoiced ??
      desiredFunding?.verdeo_funding_paid?.invoiced ??
      desiredFunding?.verdeoFundingInvoiced ??
      desiredFunding?.verdeo_funding_invoiced ??
      null
  );
};

const fallbackVerdeoFinancer = {
  id: "verdeo",
  fullname: "Verdeo",
  structureName: "Verdeo",
};

const resolveVerdeoFinancer = (
  desiredFunding: any,
  projectInvoices: any[],
  index: number
) => {
  const paidCandidate =
    desiredFunding?.indicatorValue?.verdeoFundingPaid ??
    desiredFunding?.indicatorValue?.verdeo_funding_paid ??
    desiredFunding?.verdeoFundingPaid ??
    desiredFunding?.verdeo_funding_paid ??
    null;
  const financerFromPaid = readFinancerFromPaid(paidCandidate);
  if (financerFromPaid) return financerFromPaid;

  const invoicedId = readInvoicedIdFromDesiredFunding(desiredFunding);
  if (invoicedId !== null) {
    const matchingInvoice = projectInvoices.find(
      (invoice) => parseIdLike(invoice?.id ?? invoice?.["@id"]) === invoicedId
    );
    const financerFromInvoice = normalizeFinancer(
      matchingInvoice?.financer ?? matchingInvoice?.financier
    );
    if (financerFromInvoice) return financerFromInvoice;
  }

  const fallbackInvoice = projectInvoices[index] ?? projectInvoices[0] ?? null;
  const fallbackInvoiceFinancer = normalizeFinancer(
    fallbackInvoice?.financer ?? fallbackInvoice?.financier
  );
  return fallbackInvoiceFinancer ?? fallbackVerdeoFinancer;
};

const toNumberOrZero = (value: any): number => {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : 0;
};

export const isProjectSoldForFundingDisplay = (project: any): boolean => {
  const soldStatusValue = Number(project?.soldStatus);
  return soldStatusValue === 1 || soldStatusValue === 2;
};

export const buildFundingDisplayState = (project: any) => {
  const obtainedFunding = asArray(project?.obtainedFunding);
  const desiredFunding = asArray(project?.desiredFunding);
  const isSoldProject = isProjectSoldForFundingDisplay(project);
  const shouldFlattenSoldFunding = Boolean(project?.legacySoldFundingDisplay);

  if (!isSoldProject || !shouldFlattenSoldFunding) {
    return {
      isSoldProject,
      displayedObtainedFunding: obtainedFunding,
      displayedDesiredFunding: desiredFunding,
    };
  }

  const invoices = asArray(project?.verdeoFundingInvoiceds);
  const desiredAsObtained = desiredFunding.map((desired: any, index: number) => {
    const sourceId = parseIdLike(desired?.id) ?? index + 1;
    const financer = resolveVerdeoFinancer(desired, invoices, index);

    return {
      ...desired,
      id: -1000000 - sourceId - index,
      amount: toNumberOrZero(desired?.amount),
      devise:
        desired?.devise ??
        project?.country?.currency ?? {
          symbol: "€",
          code: "EUR",
        },
      media: asArray(desired?.media),
      financer,
      foundByVerdeo: true,
      desiredFundingSourceId: sourceId,
      sourceDesiredFundingId: sourceId,
      soldFundingDisplayOnly: true,
    };
  });

  return {
    isSoldProject: true,
    displayedObtainedFunding: [...obtainedFunding, ...desiredAsObtained],
    displayedDesiredFunding: [],
  };
};
